/**
 * Configurações → a integração do SGR (spec 032, §8 e §10), no bloco da
 * sincronização de técnicos. Quem liga é quem tem `clients.manage`:
 *
 * - o estado da sessão deste processo ("login recusado" diz O QUE conferir,
 *   nunca um valor);
 * - o cliente de VENDA DIRETA (resposta 1 do operador): o ponto genérico, em
 *   que a PoolTrack vende direto para um CPF, liga a ele — por um clique;
 * - os PONTOS DE VENDA: a lista vem do SGR (relida ao abrir quando passou de
 *   24 h), com a sugestão por nome. A sugestão NUNCA liga sozinha: ligar é um
 *   clique, e a tela mostra ANTES quantos recursos e quantos tickets abertos
 *   seriam afetados. Os tickets não mudam de cliente.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { SGR_ROUTINE_STOP_LABELS, type SgrSalePoint, type SgrStatus } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { errorMessage } from '@/features/tickets/ticket-format';
import { useTenantProfile } from '@/shared/tenant-profile';
import {
  salePointPreviewQuery,
  salePointsQuery,
  sgrRoutineQuery,
  sgrSettingsQuery,
  useLinkSalePoint,
  useSetDirectSaleClient,
  useStartSgrRoutine,
  useSyncSalePoints,
} from './sgr-queries';
import { routineCountsText, unavailableText, updatedAgoText } from './sgr-view';

const SUGGESTION_KIND: Record<string, string> = {
  alias: 'pelo apelido do worker',
  exact: 'nome igual',
  approximate: 'nome parecido — confira',
};

export function sessionText(status: SgrStatus): string {
  if (!status.enabled) {
    return status.missingConfig.length > 0
      ? `Desligada — falta ou é inválida: ${status.missingConfig.join(', ')}.`
      : 'Desligada.';
  }
  const s = status.session;
  if (s.state === 'open') return `Conectada — sessão aberta ${s.at ? formatDateTime(s.at) : ''}.`;
  if (s.state === 'failed') {
    if (s.reason === 'login_failed') return 'Login recusado — confira o usuário e a senha da integração no cofre.';
    if (s.reason === 'api_denied') return 'A API recusou a integração — o usuário precisa da permissão da API na origem.';
    return `A última leitura falhou (${s.reason ?? 'motivo desconhecido'}).`;
  }
  return 'Sessão ainda não aberta — abre na primeira busca.';
}

export function SgrIntegrationCard({ status }: { status: SgrStatus }) {
  const source = status.sourceName;
  const [pendingOnly, setPendingOnly] = useState(true);
  const listQ = useQuery(salePointsQuery(pendingOnly));
  const settingsQ = useQuery(sgrSettingsQuery());
  const sync = useSyncSalePoints();
  const setDirect = useSetDirectSaleClient();
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [editingDirect, setEditingDirect] = useState(false);
  const autoSynced = useRef(false);

  const runSync = () =>
    sync.mutate(undefined, {
      onSuccess: (res) =>
        setSyncNote(res.status === 'ok' ? `${res.count} pontos lidos do ${source}.` : unavailableText(source, res.reason)),
      onError: (e) => setSyncNote(errorMessage(e)),
    });

  // a lista passou de 24 h (ou nunca foi lida): relê ao abrir, uma vez
  useEffect(() => {
    if (!listQ.data?.stale || autoSynced.current || !status.enabled) return;
    autoSynced.current = true;
    runSync();
  }, [listQ.data?.stale, status.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const direct = settingsQ.data?.directSaleClient ?? null;

  return (
    <section className="mt-4 space-y-3 rounded-md border bg-muted/30 p-3 text-sm" aria-label={`Integração ${source}`}>
      <div>
        <p className="font-semibold">{`Integração ${source}`}</p>
        <p className="text-muted-foreground">{sessionText(status)}</p>
      </div>

      <SgrRoutineBlock />

      <div className="space-y-1.5">
        <p className="font-medium">Cliente de venda direta</p>
        <p className="text-xs text-muted-foreground">
          O ponto de venda genérico (venda direta para um CPF) liga a este cliente — também por um clique, na lista abaixo.
        </p>
        {editingDirect ? (
          <div className="max-w-md">
            <ClientPicker
              selected={direct}
              onSelect={(c: ClientRef | null) =>
                setDirect.mutate(c?.id ?? null, { onSuccess: () => setEditingDirect(false) })
              }
            />
          </div>
        ) : (
          <p className="flex items-center gap-2">
            <span>{direct?.name ?? <span className="text-muted-foreground">não configurado</span>}</span>
            <Button size="xs" variant="ghost" onClick={() => setEditingDirect(true)}>
              {direct ? 'trocar' : 'configurar'}
            </Button>
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-medium">Pontos de venda</p>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
            só os sem cliente
          </label>
          <span className="text-xs text-muted-foreground">
            {listQ.data ? `lista ${updatedAgoText(listQ.data.syncedAt).replace('atualizado', 'lida')}` : ''}
          </span>
          <Button size="xs" variant="outline" disabled={sync.isPending || !status.enabled} onClick={runSync}>
            {sync.isPending ? <Spinner className="size-3" /> : <RefreshCw />}
            {`Reler do ${source}`}
          </Button>
        </div>
        {syncNote ? <p className="text-xs text-muted-foreground">{syncNote}</p> : null}
        {listQ.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : listQ.isError ? (
          <EmptyState className="h-16" message={`Erro ao carregar: ${errorMessage(listQ.error)}`} />
        ) : listQ.data.items.length === 0 ? (
          <EmptyState className="h-16" message={pendingOnly ? 'Todos os pontos estão ligados.' : 'Nenhum ponto lido ainda.'} />
        ) : (
          <ul className="divide-y rounded-md border bg-background">
            {listQ.data.items.map((p) => (
              <SalePointRow key={p.id} point={p} direct={direct} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SalePointRow({ point, direct }: { point: SgrSalePoint; direct: ClientRef | null }) {
  const [target, setTarget] = useState<ClientRef | null | undefined>(undefined);
  const [choosing, setChoosing] = useState(false);
  const link = useLinkSalePoint();
  const previewQ = useQuery({
    ...salePointPreviewQuery(point.id, target?.id ?? null),
    enabled: target !== undefined,
  });

  const confirm = () => {
    if (target === undefined) return;
    link.mutate(
      { id: point.id, clientId: target?.id ?? null },
      {
        onSuccess: () => {
          setTarget(undefined);
          setChoosing(false);
        },
      }
    );
  };

  return (
    <li className="space-y-1.5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">{point.name ?? `#${point.sgrId}`}</span>
        <span className="text-xs text-muted-foreground">{`${point.vehiclesInErp} no ERP`}</span>
        <span className="text-xs">
          {point.client ? (
            <>
              {`→ ${point.client.name}`}
              {point.directSale ? <span className="ml-1 text-muted-foreground">(venda direta)</span> : null}
            </>
          ) : (
            <span className="text-amber-700 dark:text-amber-300">sem cliente</span>
          )}
        </span>
        <span className="ml-auto flex gap-1">
          {!point.client && point.suggestion ? (
            <Button size="xs" variant="outline" onClick={() => setTarget(point.suggestion!.client)}>
              {`Usar sugestão: ${point.suggestion.client.name}`}
            </Button>
          ) : null}
          {direct && point.client?.id !== direct.id ? (
            <Button size="xs" variant="ghost" onClick={() => setTarget(direct)}>
              Venda direta
            </Button>
          ) : null}
          <Button size="xs" variant="ghost" onClick={() => setChoosing((v) => !v)}>
            {point.client ? 'Trocar' : 'Ligar…'}
          </Button>
          {point.client ? (
            <Button size="xs" variant="ghost" onClick={() => setTarget(null)}>
              Desligar
            </Button>
          ) : null}
        </span>
      </div>
      {point.suggestion && !point.client ? (
        <p className="text-xs text-muted-foreground">{`Sugestão (${SUGGESTION_KIND[point.suggestion.kind] ?? point.suggestion.kind}) — nunca liga sozinha.`}</p>
      ) : null}
      {choosing ? (
        <div className="max-w-md">
          <ClientPicker selected={null} onSelect={(c) => c && setTarget(c)} />
        </div>
      ) : null}
      {target !== undefined ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-700 dark:bg-amber-950/40" role="alert">
          <p>
            {target ? `Ligar a ${target.name}` : 'Desligar o ponto'}
            {previewQ.data
              ? ` move ${previewQ.data.resources} recurso(s) para ${target ? target.name : 'sem cliente'}; ${previewQ.data.openTickets} ticket(s) aberto(s) ligados a eles NÃO mudam de cliente.`
              : previewQ.isPending
                ? ' — contando…'
                : ''}
          </p>
          <div className="mt-1.5 flex gap-2">
            <Button size="xs" disabled={link.isPending || !previewQ.data} onClick={confirm}>
              {link.isPending ? <Spinner className="size-3" /> : null}
              Confirmar
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setTarget(undefined)}>
              Cancelar
            </Button>
          </div>
          {link.isError ? <p className="mt-1 text-destructive">{errorMessage(link.error)}</p> : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * A rotina noturna do Recurso (spec 035), só admin: a última rodada (com as
 * contagens, parciais enquanto roda) e o "rodar agora" — uma chamada à origem
 * por vez, com o limite de leituras da noite. Quem não é admin recebe 403 e o
 * bloco não aparece. O nome da origem vem da API.
 */
function SgrRoutineBlock() {
  const terms = useTenantProfile().terms;
  const q = useQuery(sgrRoutineQuery());
  const start = useStartSgrRoutine();
  const data = q.data;
  if (!data || !data.available) return null;
  const run = data.lastRun;
  const running = run?.status === 'running';
  return (
    <div className="space-y-1.5" aria-label="Rotina diária do Recurso">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-medium">Rotina diária do Recurso</p>
        {data.enabled ? (
          <Button size="xs" variant="outline" disabled={running || start.isPending} onClick={() => start.mutate()}>
            {running || start.isPending ? <Spinner className="size-3" /> : <RefreshCw />}
            Rodar agora
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        {data.enabled
          ? `Liga o ${terms.resource} dos ${terms.tickets} abertos que trazem o código da origem, ${data.scheduleLabel}, com até ${data.readsPerNight} leituras do ${data.sourceName} por noite. Preenche só campos vazios; depois disso, o que mudar no ${data.sourceName} vira aviso no ${terms.ticket}.`
          : `Desligada neste ambiente — falta ou é inválida: ${data.missingConfig.join(', ')}.`}
      </p>
      {run ? (
        <div className="space-y-0.5 text-xs text-muted-foreground">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Última rodada: {formatDateTime(run.startedAt)}</span>
            <span>{run.trigger === 'manual' ? `· manual${run.triggeredBy ? `, por ${run.triggeredBy.name}` : ''}` : '· agendada'}</span>
            {running ? (
              <Badge variant="info">Rodando…</Badge>
            ) : run.status === 'ok' ? (
              <Badge variant="success">Concluída</Badge>
            ) : (
              <Badge variant="destructive">Parou</Badge>
            )}
          </p>
          {run.counts ? <p>{routineCountsText(run.counts, terms)}</p> : null}
          {run.stoppedBy ? <p>{SGR_ROUTINE_STOP_LABELS[run.stoppedBy]}</p> : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Nenhuma rodada ainda.</p>
      )}
      {start.error ? <p className="text-xs text-destructive">{errorMessage(start.error)}</p> : null}
    </div>
  );
}
